import React, { useState } from 'react'
import classNames from 'classnames'
import { Dropdown, Checkbox, Typography, Button } from 'antd'
import { getTempEnvParam } from 'env'
import { errorTranslationMessage } from 'utils/ui'

import { useTranslation } from 'react-i18next'
import { autoLockOptions, clipboardClearOptions } from 'popups/Settings'
import { CLIPBOARD_AUTO_CLEAR_ENABLED } from 'utils/clipboardClear'
import SettingsService from '../api/Settings.ts'
import ChangeMasterPasswordModal from './ChangeMasterPasswordModal'
import { showStaleSessionPopup } from './staleSessionPopup'
import styles from '../style.module.scss'

import arrowIcon from '@/assets/images/cheveron-down-24.svg'
import exclamationGrayIcon from '@/assets/images/exclamation-circle-gray-16.svg'

const { Link } = Typography

// 백엔드 recovery_option enum 문자열 (pUtils import = myNet 체인 회피, RegisterMasterPW 와 동일 패턴)
const RECOVERY_NONE = 'NONE'
const RECOVERY_LOCAL = 'LOCAL'
const RECOVERY_ESCROW = 'ESCROW'

// 복구키 이메일 전송 옵션을 사용자에게 노출하지 않음 — 문구+체크박스만 숨김(핸들러/로직은 그대로 유지).
// 재노출 필요 시 true 로만 변경.
const SHOW_RECOVERY_KEY_EMAIL_OPTION = false

// 마스터 비밀번호 변경 UI 노출 플래그 — 모달/로직은 그대로 유지하고
// 진입 버튼만 이 플래그로 토글한다.
const SHOW_CHANGE_MASTER_PASSWORD = true

const SecurityRender = (props) => {
  const {
    autoLock,
    onAutoLockChange,
    clipboardClear,
    onClipboardClearChange,
    messageApi,
    recoveryChecked,
    setRecoveryChecked,
    locale,
    umkDuration,
    loading,
    setLoading,
    recoveryOption,
    onRecoveryOptionChange,
    wrkpInitialized,
  } = props

  const milliseconds = () => {
    if (umkDuration !== '0') {
      return Number(umkDuration) * 60 * 1000
    }
    return '0'
  }

  const { t } = useTranslation()
  const [isChangeMPWOpen, setIsChangeMPWOpen] = useState(false)
  const menuItems = autoLockOptions(t).map((option) => ({
    key: option.key,
    label: option.label,
    onClick: () => {
      onAutoLockChange(option.key)
    },
  }))

  const autoLockDropdownAttrs = {
    menu: {
      items: menuItems,
      selectable: true,
      selectedKeys: [autoLock || '0'],
    },
    overlayClassName: classNames('menuStyle2', 'contextMenu'),
    className: classNames(styles.autoLockDropdown),
    trigger: ['click'],
  }

  // 클립보드 유지시간 드롭다운 (auto_lock 형제). key='0'=Never.
  const clipboardClearMenuItems = clipboardClearOptions(t).map((option) => ({
    key: option.key,
    label: option.label,
    onClick: () => {
      onClipboardClearChange(option.key)
    },
  }))

  const clipboardClearDropdownAttrs = {
    menu: {
      items: clipboardClearMenuItems,
      selectable: true,
      selectedKeys: [clipboardClear || '0'],
    },
    overlayClassName: classNames('menuStyle2', 'contextMenu'),
    className: classNames(styles.autoLockDropdown),
    trigger: ['click'],
  }

  // Recovery key location 드롭다운: None/Local + Server.
  // Server 는 redux user.wrkp_initialized(=/me 응답) true 일 때만 노출 — 워크스페이스 복구키 미초기화 시 숨김.
  // 아이템 라벨은 기존 common.recovery.* 풀문장(Figma 843-2329와 동일, 3언어 존재).
  const recoveryItemLabelMap = {
    [RECOVERY_NONE]: t('common.recovery.none'),
    [RECOVERY_LOCAL]: t('common.recovery.local'),
    [RECOVERY_ESCROW]: t('common.recovery.server'),
  }
  const recoveryOptionItems = [
    { key: RECOVERY_NONE, label: recoveryItemLabelMap[RECOVERY_NONE] },
    { key: RECOVERY_LOCAL, label: recoveryItemLabelMap[RECOVERY_LOCAL] },
    ...(wrkpInitialized === true
      ? [{ key: RECOVERY_ESCROW, label: recoveryItemLabelMap[RECOVERY_ESCROW] }]
      : []),
  ].map((item) => ({
    ...item,
    onClick: () => onRecoveryOptionChange(item.key),
  }))

  // 선택된 값은 짧은 라벨(Not enabled / Local machine / Server)로 표시 (Figma 846-2355).
  // 현재값 라벨은 게이팅과 무관하게 표시(이미 ESCROW 인데 wrkp 미초기화 환경에서도 정확히)
  const recoveryLocationLabelMap = {
    [RECOVERY_NONE]: t('common.recovery.locationNone'),
    [RECOVERY_LOCAL]: t('common.recovery.locationLocal'),
    [RECOVERY_ESCROW]: t('common.recovery.locationServer'),
  }
  const recoveryOptionLabel =
    recoveryLocationLabelMap[recoveryOption] || recoveryLocationLabelMap[RECOVERY_NONE]

  const recoveryOptionDropdownAttrs = {
    menu: {
      items: recoveryOptionItems,
      selectable: true,
      selectedKeys: [recoveryOption || RECOVERY_NONE],
    },
    overlayClassName: classNames('menuStyle2', 'contextMenu'),
    className: classNames(styles.autoLockDropdown),
    trigger: ['click'],
  }

  const handleCheckboxChange = async (e) => {
    const { checked: checked2 } = e.target
    console.log('handleCheckboxChange - ', checked2)

    const res = await SettingsService.put({
      recoveryKeyToEmail: checked2 === true && checked2 != null ? 'email' : 'local',
    })

    if (res.code === 200) {
      setRecoveryChecked(checked2)
    }

    if (res.code === 620) {
      const res2 = await SettingsService.create({
        recoveryKeyToEmail: checked2 === true && checked2 != null ? 'email' : 'local',
        autoLock: milliseconds(),
        locale: locale.substring(0, 2),
      })
      console.log('res2 - ', res2)
    }
  }

  // LOCAL 복구키 발급/재발급(PEM 분실 사용자 포함) — 새 키 생성 후 기존 enc_umk 덮어쓰기.
  const handleCreateRecoveryKey = async () => {
    setLoading(true)
    const rsaObj = getTempEnvParam('rsaObj')

    try {
      const response = await rsaObj.createRecoveryKey()

      if (response === 200) {
        messageApi.success(t('appConfig.settings.successRecoveryKey'))
      } else if (response === 845) {
        // 845(세션 UMK 세대 불일치): 캐시 UMK 가 옛 마스터PW 세대(다른 기기에서 변경됨).
        // 토스트+강제 이동 대신 이동을 사용자 선택에 맡기는 안내 팝업으로 통일
        // (Settings/index.js recovery_option 845 · 패스키 stale 팝업과 동일 UX).
        showStaleSessionPopup(t, {
          title: t('common.title.warning'),
          content: t('workspace.error.845'),
        })
      } else {
        messageApi.error(t('appConfig.settings.failedRecoveryKey'))
      }
    } catch (error) {
      console.log('error - ', error)
      errorTranslationMessage(error.code, t, messageApi)
    } finally {
      setLoading(false)
    }
  }

  const autoLockOption = autoLockOptions(t).find((option) => option.key === autoLock)?.label
  const clipboardClearOption = clipboardClearOptions(t).find(
    (option) => option.key === (clipboardClear || '0'),
  )?.label
  return (
    <div className={classNames(styles.securityContent)}>
      <div className={classNames(styles.tabItem)}>
        <div className={classNames(styles.content)}>
          <span>{t('appConfig.settings.autoLock')}</span>
          <Dropdown {...autoLockDropdownAttrs}>
            <div>
              <span>{autoLock ? autoLockOption : t('appConfig.settings.off')}</span>
              <img src={arrowIcon} alt="icons" style={{ width: '24px', height: '24px' }} />
            </div>
          </Dropdown>
        </div>
      </div>
      {/* 클립보드 자동삭제 유지시간 (auto_lock 형제). 권한0 best-effort — 포커스/복귀 시 삭제. */}
      {/* OPD-22: 다른 앱 사용 중 OS 클립보드 삭제 불가(Native Messaging 필요) → 기능 보류, UI 숨김. */}
      {CLIPBOARD_AUTO_CLEAR_ENABLED && (
        <div className={classNames(styles.tabItem)}>
          <div className={classNames(styles.content)}>
            <span>{t('appConfig.settings.clipboardClear')}</span>
            <Dropdown {...clipboardClearDropdownAttrs}>
              <div>
                <span>{clipboardClearOption || t('appConfig.settings.clipboardNever')}</span>
                <img src={arrowIcon} alt="icons" style={{ width: '24px', height: '24px' }} />
              </div>
            </Dropdown>
          </div>
        </div>
      )}
      {/* 자동 잠금 시간 아래 구분선 — passkey 비활성화 상태에서도 상시 노출 */}
      <div className={classNames(styles.passkeySeparator)} />
      {/* 마스터 비밀번호 변경 (Figma — Recovery key location 위) — SHOW_CHANGE_MASTER_PASSWORD 플래그로 게이팅 */}
      {SHOW_CHANGE_MASTER_PASSWORD && (
        <div className={classNames(styles.tabItem)}>
          <div className={classNames(styles.content)}>
            <span>{t('login.title.masterPW')}</span>
            <Button
              className={classNames('h38', styles.changePasswordBtn)}
              onClick={() => setIsChangeMPWOpen(true)}
            >
              {t('common.button.change')}
            </Button>
          </div>
        </div>
      )}
      {/* 복구 키 저장 위치 (Figma 846-2355) — 라벨 + 테두리형 드롭다운 트리거 */}
      <div className={classNames(styles.recoveryLocationRow)}>
        <p className={classNames(styles.recoveryLocationLabel)}>
          {t('common.recovery.locationTitle')}
        </p>
        <Dropdown {...recoveryOptionDropdownAttrs}>
          <div className={classNames(styles.recoveryLocationTrigger)}>
            <span className={classNames(styles.recoveryLocationValue)}>{recoveryOptionLabel}</span>
            <img src={arrowIcon} alt="icons" />
          </div>
        </Dropdown>
      </div>
      {/* 현재 상태 안내 (Figma 814-617) — LOCAL 상태일 때만 새 복구 키 생성 링크 노출 */}
      <div className={classNames(styles.recoveryStatus)}>
        <div className={classNames(styles.recoveryStatusRow)}>
          <img src={exclamationGrayIcon} alt="" className={classNames(styles.recoveryStatusIcon)} />
          <div className={classNames(styles.recoveryStatusText)}>
            {recoveryOption === RECOVERY_LOCAL && <p>{t('common.recovery.statusLocal')}</p>}
            {recoveryOption === RECOVERY_ESCROW && <p>{t('common.recovery.statusServer')}</p>}
            {recoveryOption !== RECOVERY_LOCAL && recoveryOption !== RECOVERY_ESCROW && (
              <React.Fragment>
                <p>{t('common.recovery.statusNone1')}</p>
                <p>{t('common.recovery.statusNone2')}</p>
              </React.Fragment>
            )}
            {recoveryOption === RECOVERY_LOCAL && (
              <Link
                className={classNames(styles.recoveryCreateLink)}
                onClick={handleCreateRecoveryKey}
                disabled={loading}
              >
                {t('common.recovery.createNewKey')}
              </Link>
            )}
          </div>
        </div>
        {/* 복구키 이메일 전송 옵션 노출 차단 — 문구+체크박스를 사용자에게 표시하지 않음(핸들러/로직 유지). */}
        {recoveryOption === RECOVERY_LOCAL && SHOW_RECOVERY_KEY_EMAIL_OPTION && (
          <Checkbox onChange={handleCheckboxChange} checked={recoveryChecked}>
            {t('appConfig.settings.sendRecoveryKeyToEmail')}
          </Checkbox>
        )}
      </div>
      {/* 패스키 섹션은 Settings 의 전용 '패스키' 탭으로 이동 (Figma 685-559) */}
      <ChangeMasterPasswordModal
        open={isChangeMPWOpen}
        onClose={() => setIsChangeMPWOpen(false)}
        messageApi={messageApi}
      />
    </div>
  )
}

export default SecurityRender
